// src/services/inventoryApi.ts
import apiClient, { BASE_URL } from "./apiClient";
export type AlertChannel = "inapp" | "email" | "push";

export type LowStock = {
  enabled: boolean;
  threshold?: number;
  alertOnce?: boolean;
  cooldownHours?: number;
  channels?: AlertChannel[];
};
export interface IInventory {
  _id: string;
  itemName: string;
  quantity: number;
  costPerUnit?: number;
  brand?: string;
  defaultSellingPrice?: number;
  imageUrl?: string;
  category?: string;
  categoryId?: string | { _id?: string; name?: string; scope?: string };
  businessName?: string;
  notes?: string;
  status: "active" | "discontinued" | "out of stock";
  sku?: string;
  variants?: Array<{ attribute: string; value: string }>;
  lowStock?: LowStock;
}
interface GetAllInventoryResponse {
  items: IInventory[];
  totalCost: number;
  totalSellingPrice: number;
}
export const getAllInventory = () =>
  apiClient.get<GetAllInventoryResponse>("/inventory");

export const createInventory = (data: Partial<IInventory>) =>
  apiClient.post<IInventory>("/inventory", data);

export const updateInventory = (id: string, data: Partial<IInventory>) =>
  apiClient.put<IInventory>(`/inventory/${id}`, data);

export const deleteInventory = (id: string) =>
  apiClient.delete(`/inventory/${id}`);

export const uploadInventoryImage = async (
  image: File,
  itemData: Partial<IInventory>,
  token: string,
): Promise<IInventory> => {
  const formData = new FormData();

  formData.append("image", image, image.name);

  Object.keys(itemData).forEach((key) => {
    const value = (itemData as any)[key];
    if (key === "variants" || key === "lowStock") {
      formData.append(key, JSON.stringify(value ?? null));
    } else {
      formData.append(key, value as any);
    }
  });

  const response = await fetch(`${BASE_URL}/inventory/upload`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${errorText}`);
  }

  return response.json();
};

export const updateInventoryImage = async (
  id: string,
  image: File,
  token: string,
): Promise<{ message: string; inventory: IInventory }> => {
  const formData = new FormData();
  formData.append("image", image, image.name);

  const response = await fetch(`${BASE_URL}/inventory/${id}/upload-image`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Image update failed: ${errorText}`);
  }

  return response.json();
};

// Use the shared client so imports and downloads refresh expired access tokens.
const downloadSpreadsheet = async (path: string): Promise<Response> => {
  const response = await apiClient.get(path, {
    responseType: "arraybuffer",
    params: { _ts: Date.now() },
  });
  return new Response(response.data, { headers: {
    "content-type": String(response.headers["content-type"] || "application/octet-stream"),
    "content-disposition": String(response.headers["content-disposition"] || ""),
  } });
};
export const downloadInventorySample = (_token: string) => downloadSpreadsheet("/inventory-import/sample");
export const downloadCurrentInventory = (_token: string) => downloadSpreadsheet("/inventory-import/export");
export const downloadTransactionReport = (eventId: string, _token: string) =>
  downloadSpreadsheet(`/transactions/events/${eventId}/export`);

export interface InventoryImportSummary {
  generatedSkuCount?: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  total: number;
  message: string;
}
const sendInventoryFile = async (file: File, update: boolean, preview: boolean) => {
  const formData = new FormData();
  formData.append("excelFile", file);
  const { data } = await apiClient.post<InventoryImportSummary>(
    `/inventory-import/${preview ? "preview" : update ? "update" : "upload"}`,
    formData,
    { params: { mode: update ? "update" : "add" } },
  );
  return data;
};
export const uploadInventoryExcel = (file: File, _token: string, preview = false) =>
  sendInventoryFile(file, false, preview);
export const updateInventoryExcel = (file: File, _token: string, preview = false) =>
  sendInventoryFile(file, true, preview);
